import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaPlus, FaTrash } from "react-icons/fa";
import { getRequest, putRequest } from "../../helpers/api.helper";
import { useLanguage } from "../../context/LanguageContext";
import { ProfileType } from "../../types";

const MAX_TEXT_BOXES = 10;

const Step10Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [textBoxes, setTextBoxes] = useState<{ id: number; text: string }[]>(
    [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState<number | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!id) {
        setIsLoadingProfile(false);
        return;
      }

      try {
        setIsLoadingProfile(true);
        const response = await getRequest(`/bureau/profile/${id}`);
        if (response.success && response.data) {
          setProfile(response.data);
          return;
        }

        setProfile(null);
        
      } catch (error) {
        console.error("Error loading profile:", error);
        setProfile(null);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [id]);

  useEffect(() => {
    if (profile?.customWords && profile?.customWords?.length > 0) {
      setTextBoxes(
        profile.customWords.map((text: string, index: number) => ({
          id: index + 1,
          text,
        })),
      );
    } else {
      setTextBoxes([{ id: 1, text: "" }]);
    }
  }, [profile]);

  if (isLoadingProfile) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <p className="text-gray-700 font-semibold text-lg">
            {t("Loading profile")}...
          </p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <p className="text-gray-700 font-semibold text-lg">
            {t("Profile data not available")}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            {t("Go Back")}
          </button>
        </div>
      </div>
    );
  }

  const addTextBox = () => {
    if (textBoxes.length < MAX_TEXT_BOXES) {
      const newId = Math.max(...textBoxes.map((b) => b.id), 0) + 1;
      setTextBoxes([...textBoxes, { id: newId, text: "" }]);
    }
  };

  const removeTextBox = async (boxId: number) => {
    if (textBoxes.length > 1) {
      setIsRemoving(boxId);
      const updatedTextBoxes = textBoxes.filter((b) => b.id !== boxId);

      try {
        const customWords = updatedTextBoxes
          .filter((b) => b.text.trim() !== "")
          .map((b) => b.text.trim());

        const response = await putRequest(`/bureau/profile/${profile._id}`, {
          customWords,
        });

        if (response.success) {
          setTextBoxes(updatedTextBoxes);
        } else {
          alert("Failed to remove description. Please try again.");
        }
      } catch (err) {
        console.error("Error removing description:", err);
        alert("Error removing description. Please try again.");
      } finally {
        setIsRemoving(null);
      }
    }
  };

  const updateTextBox = (boxId: number, text: string) => {
    setTextBoxes(textBoxes.map((b) => (b.id === boxId ? { ...b, text } : b)));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const customWords = textBoxes
        .filter((b) => b.text.trim() !== "")
        .map((b) => b.text.trim());

      const response = await putRequest(`/bureau/profile/${profile._id}`, {
        customWords,
        step10: 1,
      });

      if (response.success) {
        alert("Profile description updated successfully!");
        navigate(-1);
      } else {
        alert("Failed to update. Try again.");
      }
    } catch (err: any) {
      console.error("Error updating profile:", err);
      alert(err?.message || "Error updating profile.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="profile-edit-page min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full z-50 bg-gradient-to-r from-red-600 to-red-700 fixed top-0 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              className="flex items-center gap-2 text-white hover:bg-white/10 px-3 py-2 rounded-lg transition"
              onClick={() => navigate(-1)}
            >
              <FaArrowLeft />
              <span className="font-medium">{t("Back")}</span>
            </button>
            <div className="text-center flex-1">
              <h4 className="text-lg font-bold text-white">
                {t("Profile Description")}
              </h4>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      <div className="pt-24 pb-12 px-2">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-5">
            {textBoxes.map((box, index) => (
              <div
                key={box.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 px-3 py-2 border border-gray-200"
              >
                <div className="flex justify-between items-center mb-3">
                  <label className="font-semibold text-gray-800 text-base">
                    {t("Description")} {index + 1}
                  </label>
                  {textBoxes.length > 1 && (
                    <button
                      onClick={() => removeTextBox(box.id)}
                      disabled={isRemoving === box.id}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition ${
                        isRemoving === box.id
                          ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                          : "text-red-500 hover:text-red-700 hover:bg-red-50"
                      }`}
                    >
                      {isRemoving === box.id ? "Removing..." : "Remove"}
                    </button>
                  )}
                </div>
                <textarea
                  value={box.text}
                  onChange={(e) => updateTextBox(box.id, e.target.value)}
                  placeholder="Write about the profile..."
                  rows={3}
                  maxLength={500}
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-gray-700 placeholder-gray-400 transition"
                />
                <div className="flex justify-end mt-2">
                  <span
                    className={`text-xs font-medium ${
                      box.text.length > 450 ? "text-red-600" : "text-gray-500"
                    }`}
                  >
                    {box.text.length} / 500
                  </span>
                </div>
              </div>
            ))}
          </div>

          {textBoxes.length < MAX_TEXT_BOXES && (
            <div className="text-center mt-6">
              <button
                onClick={addTextBox}
                className="inline-flex items-center gap-2 bg-white border-2 border-green-600 text-green-600 px-6 py-3 rounded-lg hover:bg-green-600 hover:text-white font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <FaPlus size={14} />
                <span>{t("Add Another Description")}</span>
              </button>
            </div>
          )}

          <div className="mt-10 flex justify-center">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-10 py-4 rounded-lg text-white font-bold text-lg shadow-lg transition-all duration-200 ${
                isSaving
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl transform hover:-translate-y-0.5"
              }`}
            >
              {isSaving
                ? "Saving Changes..."
                : "Save Changes"}
            </button>
          </div>

          <p className="text-center text-xs text-gray-500 mt-4">
            {t("Your changes will be saved to the profile")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Step10Edit;
